/**
 * ZakazniciSpravaPage — admin tools of the Zákazníci module (access overhaul
 * Stage 5: Management dissolution).
 *
 * One TanStack route at `/zakaznici/sprava` hosts the customer dedup
 * workspace that used to live under `/management?tab=dedup`. Single tool
 * today, so there is no `?tab` dispatch — the crown subtab strip mirrors the
 * list page's (Zákazníci · Vyhledávání · Správa).
 *
 * Gating mirrors the API (`/v1/management/customers/*` now requires
 * zakaznici:admin): the page shows an access notice once membership resolves
 * without `can('zakaznici', 'admin')`. Until it resolves we render
 * optimistically (same philosophy as AppRail / CommandPalette) — the server
 * is the boundary.
 */

import { useNavigate } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Shell } from '@/components/Shell';
import { Subtabs } from '@/components/Subtabs';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useDedupLogQuery } from '@/modules/management/api';
import { DedupWorkspace } from './DedupWorkspace';

type Subtab = 'zakaznici' | 'vyhledavani' | 'sprava';

export function ZakazniciSpravaPage() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  // Optimistic until membership resolves — the API is the hard boundary.
  const canAdmin = !resolved || can('zakaznici', 'admin');

  const log = useDedupLogQuery(canAdmin, 8);

  const onSubtabChange = (next: Subtab) => {
    if (next === 'zakaznici') navigate({ to: '/zakaznici' });
    else if (next === 'vyhledavani') navigate({ to: '/zakaznici/vyhledavani' });
  };

  return (
    <Shell rail={<AppRail active="zakaznici" />}>
      <Crown>
        <Breadcrumb
          segments={[{ label: 'Zákazníci', href: '/zakaznici' }, { label: 'Správa' }]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>Správa</DisplayHeading>
          <CrownMeta
            items={[
              {
                label: 'Sloučení v logu',
                value: log.data ? formatInt(log.data.entries.length) : '—',
              },
            ]}
          />
        </Crown.Row>
        <Subtabs<Subtab>
          aria-label="Zákazníci — sekce"
          value="sprava"
          onChange={onSubtabChange}
          items={[
            { value: 'zakaznici', label: 'Zákazníci' },
            { value: 'vyhledavani', label: 'Vyhledávání' },
            { value: 'sprava', label: 'Správa' },
          ]}
        />
      </Crown>

      {!canAdmin ? (
        <div
          data-testid="zakaznici-sprava-denied"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
        >
          <EmptyState
            eyebrow="403"
            title="Správa zákazníků vyžaduje administrátorský přístup k modulu."
            description="Pro přístup požádejte správce firmy."
          />
        </div>
      ) : (
        <DedupWorkspace isAdmin={canAdmin} />
      )}
    </Shell>
  );
}
